class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (!facetForm) return;
    this.boundDebouncedSubmit = this.debouncedOnSubmit.bind(this);

    /* ZYPHER_V240_MOBILE_STAGE_FILTERS_UNTIL_APPLY
       Do not rerender the facet drawer while a shopper is selecting mobile
       filters. Live AJAX replacement was destroying the open drawer state
       and exposing old white fallback markup. */
    facetForm.addEventListener('input', (event) => {
      if (facetForm.id === 'FacetFiltersFormMobile') return;
      if (event.target.closest('price-range')) return;
      this.boundDebouncedSubmit(event);
    });

    facetForm.addEventListener('change', (event) => {
      if (facetForm.id === 'FacetFiltersFormMobile') return;
      if (!event.target.closest('price-range')) return;
      this.onSubmitHandler(event);
    });

    const mobileApply = facetForm.querySelector('[data-z240-mobile-apply]');
    if (mobileApply) {
      mobileApply.addEventListener('click', (event) => {
        event.preventDefault();
        const searchParams = this.createSearchParams(facetForm);
        FacetFiltersForm.closeMobileDrawer();
        this.onSubmitForm(searchParams, event);
      });
    }

    facetForm.addEventListener('keydown', (event) => {
      if (!event.target.closest('price-range') || event.key !== 'Enter') return;
      event.preventDefault();
      event.target.blur();
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    FacetFiltersForm.syncCustomFilterState();
    FacetFiltersForm.pruneUnavailableCards();

    const onHistoryChange = (event) => {
      if (window.location.pathname !== FacetFiltersForm.pathPrev) {
        FacetFiltersForm.renderCollectionPath(new URL(window.location.href), false);
        return;
      }
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);

    const dismissMobileDrawer = (drawer) => {
      const summary = drawer.querySelector(':scope > details > summary');
      FacetFiltersForm.closeMobileDrawer();
      if (summary) summary.focus({ preventScroll: true });
    };

    document.addEventListener('click', (event) => {
      const close = event.target.closest('[data-zypher-mobile-facets-close]');
      if (!close) return;
      const drawer = close.closest('.mobile-facets__wrapper');
      if (!drawer) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      dismissMobileDrawer(drawer);
    }, true);

    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return;
      const drawer = event.target.closest('.mobile-facets__wrapper');
      const details = drawer?.querySelector(':scope > details[open]');
      if (!details || event.target.closest('details[open]') !== details) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      dismissMobileDrawer(drawer);
    }, true);

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-zypher-filter-link]');
      const nonPrimaryClick = typeof event.button === 'number' && event.button !== 0;
      if (!link || event.defaultPrevented || nonPrimaryClick || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const targetUrl = new URL(link.href, window.location.origin);
      if (link.dataset.zypherFilterReset === 'true') targetUrl.searchParams.set('zypher_reset_filters', 'true');
      const filterParam = link.dataset.zypherFilterParam;
      const filterValue = link.dataset.zypherFilterValue;
      if (filterParam && filterValue && new URL(window.location.href).searchParams.get(filterParam) === filterValue) {
        targetUrl.searchParams.delete(filterParam);
        targetUrl.searchParams.set('zypher_clear_filter', filterParam);
      }
      const fromMobileDrawer = !!link.closest('#FacetFiltersFormMobile');
      if (fromMobileDrawer) {
        document.body.classList.add('z241-mobile-filter-applying');
        FacetFiltersForm.hardCloseMobileDrawer();
      } else {
        FacetFiltersForm.closeMobileDrawer();
      }

      const renderPromise = FacetFiltersForm.renderCollectionPath(targetUrl);

      if (fromMobileDrawer && renderPromise && typeof renderPromise.finally === 'function') {
        renderPromise.finally(() => {
          FacetFiltersForm.hardCloseMobileDrawer();
          document.body.classList.remove('z241-mobile-filter-applying');
          window.requestAnimationFrame(() => FacetFiltersForm.hardCloseMobileDrawer());
          window.setTimeout(() => FacetFiltersForm.hardCloseMobileDrawer(), 120);
        });
      }
    });
  }

  /* ZYPHER_V241_HARD_CLOSE_AFTER_CUSTOM_FILTER */
  static hardCloseMobileDrawer() {
    const active = document.activeElement;
    if (active && active.closest && active.closest('.mobile-facets__wrapper')) {
      try { active.blur(); } catch (e) {}
    }

    document.querySelectorAll('.mobile-facets__wrapper').forEach((drawer) => {
      drawer.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      drawer.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
      drawer.querySelectorAll('summary[aria-expanded="true"]').forEach((summary) => summary.setAttribute('aria-expanded','false'));
    });

    document.body.classList.remove('overflow-hidden-mobile');
    document.body.classList.remove('overflow-hidden-tablet');
    document.documentElement.style.removeProperty('--viewport-height');
    if (typeof removeTrapFocus === 'function') {
      try { removeTrapFocus(); } catch (e) {}
    }
  }

  static closeMobileDrawer() {
    document.querySelectorAll('.mobile-facets__wrapper').forEach((drawer) => {
      const details = drawer.querySelector(':scope > details');
      if (!details) return;
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      const summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      details.querySelectorAll('details[open], details.menu-opening').forEach((nestedDetails) => {
        nestedDetails.removeAttribute('open');
        nestedDetails.classList.remove('menu-opening');
      });
      details.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
      details.querySelectorAll('summary[aria-expanded="true"]').forEach((nestedSummary) => nestedSummary.setAttribute('aria-expanded', 'false'));
    });
    document.body.classList.remove('overflow-hidden-mobile');
    document.body.classList.remove('overflow-hidden-tablet');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  }

  static pruneUnavailableCards() {
    if (new URL(window.location.href).searchParams.get('filter.v.availability') !== '1') return;
    document.querySelectorAll('#product-grid [data-zypher-stock="out"]').forEach((card) => card.remove());
  }

  static syncCustomFilterState() {
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams;
    document.querySelectorAll('[data-zypher-filter-param][data-zypher-filter-value]').forEach((link) => {
      const pathToken = link.dataset.zypherFilterPathToken;
      const pathTokenAlt = link.dataset.zypherFilterPathTokenAlt;
      const activeByParam = searchParams.get(link.dataset.zypherFilterParam) === link.dataset.zypherFilterValue;
      const activeByPath = pathToken && currentUrl.pathname.includes(pathToken);
      const activeByAltPath = pathTokenAlt && currentUrl.pathname.includes(pathTokenAlt);
      const active = activeByParam || activeByPath || activeByAltPath;
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });

    document.querySelectorAll('[data-zypher-filter-path-token]:not([data-zypher-filter-param])').forEach((link) => {
      const pathToken = link.dataset.zypherFilterPathToken;
      const pathTokenAlt = link.dataset.zypherFilterPathTokenAlt;
      const active = currentUrl.pathname.includes(pathToken) || (pathTokenAlt && currentUrl.pathname.includes(pathTokenAlt));
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }


  static beginRequest() {
    FacetFiltersForm.activeRequest?.controller.abort();
    const request = { controller: new AbortController() };
    FacetFiltersForm.activeRequest = request;
    return request;
  }

  static isCurrentRequest(request) {
    return FacetFiltersForm.activeRequest === request && !request.controller.signal.aborted;
  }

  static setLoading(loading) {
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', loading ? 'true' : 'false');
    grid?.querySelector('.collection')?.classList.toggle('loading', loading);
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('loading', loading);
    });
    FacetFiltersForm.toggleActiveFacets(loading);
  }

  static fetchSection(url, request) {
    const timeout = window.setTimeout(() => request.controller.abort(), 12000);
    return fetch(url, { signal: request.controller.signal, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Filter request failed with ${response.status}`);
        return response.text();
      })
      .finally(() => window.clearTimeout(timeout));
  }

  static finishRequest(request) {
    if (FacetFiltersForm.activeRequest !== request) return;
    FacetFiltersForm.setLoading(false);
    FacetFiltersForm.activeRequest = null;
  }

  static recoverRequest(request, url) {
    if (FacetFiltersForm.activeRequest !== request) return;
    FacetFiltersForm.setLoading(false);
    FacetFiltersForm.hardCloseMobileDrawer();
    document.body.classList.remove('z241-mobile-filter-applying');
    window.location.assign(url);
  }

  static parseSection(markup) {
    return typeof markup === 'string' ? new DOMParser().parseFromString(markup, 'text/html') : markup;
  }

  static renderCollectionPath(targetUrl, updateHistory = true) {
    const request = FacetFiltersForm.beginRequest();
    const sectionId = document.getElementById('product-grid')?.dataset.id;
    if (!sectionId) {
      FacetFiltersForm.recoverRequest(request, targetUrl.href);
      FacetFiltersForm.finishRequest(request);
      return Promise.resolve();
    }

    const resetFilters = targetUrl.searchParams.get('zypher_reset_filters') === 'true';
    const searchParams = resetFilters ? new URLSearchParams() : new URLSearchParams(window.location.search);
    const clearFilter = targetUrl.searchParams.get('zypher_clear_filter');
    if (clearFilter) searchParams.delete(clearFilter);
    targetUrl.searchParams.forEach((value, key) => searchParams.set(key, value));
    searchParams.delete('zypher_clear_filter');
    searchParams.delete('zypher_reset_filters');
    searchParams.delete('page');

    const query = searchParams.toString();
    const nextUrl = `${targetUrl.pathname}${query ? `?${query}` : ''}`;
    const sectionUrl = `${targetUrl.pathname}?section_id=${encodeURIComponent(sectionId)}${query ? `&${query}` : ''}`;
    FacetFiltersForm.setLoading(true);

    return FacetFiltersForm.fetchSection(sectionUrl, request)
      .then((responseText) => {
        if (!FacetFiltersForm.isCurrentRequest(request)) return;
        const parsedHTML = FacetFiltersForm.parseSection(responseText);
        const nextGrid = parsedHTML.getElementById('ProductGridContainer');
        const currentGrid = document.getElementById('ProductGridContainer');
        if (!nextGrid || !currentGrid) throw new Error('Filtered collection markup is incomplete');

        FacetFiltersForm.hardCloseMobileDrawer();
        currentGrid.innerHTML = nextGrid.innerHTML;
        [
          ['#main-collection-filters', 'outerHTML'],
          ['.facets-vertical-sort', 'outerHTML'],
          ['.zypher-collection-seo-heading', 'innerHTML']
        ].forEach(([selector, property]) => {
          const current = document.querySelector(selector);
          const next = parsedHTML.querySelector(selector);
          if (current && next) current[property] = next[property];
        });

        if (updateHistory) history.pushState({ searchParams: query }, '', nextUrl);
        FacetFiltersForm.pathPrev = targetUrl.pathname;
        FacetFiltersForm.searchParamsPrev = query;
        FacetFiltersForm.filterData = [];
        FacetFiltersForm.hardCloseMobileDrawer();
        FacetFiltersForm.syncCustomFilterState();
        FacetFiltersForm.pruneUnavailableCards();
        if (typeof initButtons === 'function') initButtons();
        if (typeof initButtonsCompare === 'function') initButtonsCompare();
      })
      .catch(() => FacetFiltersForm.recoverRequest(request, nextUrl))
      .finally(() => FacetFiltersForm.finishRequest(request));
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }


  static renderPage(searchParams, event, updateURLHash = true) {
    const request = FacetFiltersForm.beginRequest();
    const nextUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    if (!document.getElementById('product-grid')?.dataset.id) {
      FacetFiltersForm.recoverRequest(request, nextUrl);
      FacetFiltersForm.finishRequest(request);
      return Promise.resolve();
    }
    FacetFiltersForm.setLoading(true);
    const sections = FacetFiltersForm.getSections();
    return Promise.all(sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(section.section)}&${searchParams}`;
      const filterDataUrl = element => element.url === url;
      return Promise.resolve().then(() => FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, request) :
        FacetFiltersForm.renderSectionFromFetch(url, event, request));
    }))
      .then(() => {
        if (!FacetFiltersForm.isCurrentRequest(request)) return;
        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
        FacetFiltersForm.searchParamsPrev = searchParams;
        FacetFiltersForm.pathPrev = window.location.pathname;
        FacetFiltersForm.syncCustomFilterState();
      })
      .catch(() => FacetFiltersForm.recoverRequest(request, nextUrl))
      .finally(() => FacetFiltersForm.finishRequest(request));
  }

  static renderSectionMarkup(markup, event) {
    const parsedHTML = FacetFiltersForm.parseSection(markup);
    if (!parsedHTML.getElementById('ProductGridContainer')) throw new Error('Filtered collection markup is incomplete');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
  }

  static renderSectionFromFetch(url, event, request) {
    return FacetFiltersForm.fetchSection(url, request).then((markup) => {
      if (!FacetFiltersForm.isCurrentRequest(request)) return;
      FacetFiltersForm.renderSectionMarkup(markup, event);
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.filter(item => item.url !== url), { html: markup, url }].slice(-12);
    });
  }

  static renderSectionFromCache(filterDataUrl, event, request) {
    if (!FacetFiltersForm.isCurrentRequest(request)) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSectionMarkup(html, event);
  }


  static renderProductGridContainer(markup) {
    const parsedHTML = FacetFiltersForm.parseSection(markup);
    const currentGrid = document.getElementById('ProductGridContainer');
    const nextGrid = parsedHTML.getElementById('ProductGridContainer');
    if (!currentGrid || !nextGrid) throw new Error('Filtered collection markup is incomplete');
    currentGrid.innerHTML = nextGrid.innerHTML;
    FacetFiltersForm.pruneUnavailableCards();
  }

  static renderProductCount(markup) {
    const count = FacetFiltersForm.parseSection(markup).getElementById('ProductCount');
    if (count) {
      ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
        const container = document.getElementById(id);
        if (!container) return;
        container.innerHTML = count.innerHTML;
        container.classList.remove('loading');
      });
    }
    if (typeof initButtons === 'function') initButtons();
    if (typeof initButtonsCompare === 'function') initButtonsCompare();
  }

  static renderFilters(html, event) {
    const parsedHTML = FacetFiltersForm.parseSection(html);

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    const drawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.pathPrev = window.location.pathname;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = Number(input.value);
    if (!Number.isFinite(value)) { input.value = ''; return; }
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
